import React, { FC, useEffect, useState } from "react";
import {
  Card,
  Button,
  Group,
  Accordion,
  Modal,
  ActionIcon,
  MultiSelect,
  Divider,
  Title,
  Blockquote,
} from "@mantine/core";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ProjectDataType, StudyDataType } from "@/types/apiTypes";
import ShipDataTable from "@/pages/ship/ShipDataTable";
import PlotlyGraphs from "@/pages/ship/PlotlyGraphs";
import CanvasComponent from "@/components/CanvasComponent";
import WingCardComponent from "../../components/WingCardComponent";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useDarkModeContext } from "@/hooks/contextHooks/useDarkModeContext";
import { Text } from "@/components/Generics/Text";
import { useDarkModeColors } from "@/hooks/helperHooks/useDarkModeColors";
import {
  getButtonStyles,
  getDeleteButtonStyles,
} from "@/helpers/getButtonStyles";
import { getAccordionStyles } from "@/helpers/getAccordionStyles";
import { deleteStudyApi } from "@/API/deleteStudyApi";
import { useFetchSingleStudy } from "@/hooks/apiHooks/useFetchSingleStudy";
import { useFetchProjects } from "@/hooks/apiHooks/useFetchProjects";
import { useAssociateStudyToProject } from "@/hooks/apiHooks/useAssociateStudyToProject";
import { useRemoveStudyFromProject } from "@/hooks/apiHooks/useRemoveStudyFromProject";
import { usePageTransition } from "@/hooks/helperHooks/usePageTransition";
import styles from "../../css/studyDetails.module.css";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";

// interface StudyData {
//   id: string;
//   experiment_name: string;
//   vessel_type: string;
//   vessel_size: string;
//   wing_model_used: any;
//   form_values: any;
//   result_link: string;
//   ship_id: string;
//   projects?: ProjectDataType[];
// }

const StudyDetails: FC = () => {
  // HOOK for Smooth transitions between pages
  const transition = usePageTransition();

  const { colors } = useAppColors();

  const { id } = useParams<{ id: string }>();
  // const [study, setStudy] = useState<StudyDataType | null>(null);
  // const [shipData, setShipData] = useState<any>(null);
  // const [allProjects, setAllProjects] = useState<ProjectDataType[]>([]);

  const [associatedProjects, setAssociatedProjects] = useState<
    ProjectDataType[]
  >([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [isEditingProjects, setIsEditingProjects] = useState(false);
  const [isProjectUpdateModalOpen, setIsProjectUpdateModalOpen] =
    useState(false);
  const [activeAccordionItem, setActiveAccordionItem] = useState<string | null>(
    null,
  );
  const [deleteStudyId, setDeleteStudyId] = useState<string | undefined>(id);
  const [isDeleteStudyModalOpen, setIsDeleteStudyModalOpen] = useState(false);
  const navigate = useNavigate();

  const { isDarkMode } = useDarkModeContext();
  const deleteButtonStyles = getDeleteButtonStyles(isDarkMode);
  const buttonStyles = getButtonStyles(isDarkMode);
  const accordionStyles = getAccordionStyles(isDarkMode);
  const { getColor } = useDarkModeColors();
  const {
    data: study,
    isLoading: isStudyLoading,
    isError: isStudyError,
  } = useFetchSingleStudy(id);
  const shipData = study?.ship;
  const projects = study?.projects ?? [];
  console.log(study);
  console.log(projects);

  const { data: allProjects = [], isLoading: isProjectsLoading } =
    useFetchProjects();

  const associateStudyMutation = useAssociateStudyToProject();
  const removeStudyMutation = useRemoveStudyFromProject();

  useEffect(() => {
    if (study) {
      const projects = study.projects ?? [];
      setAssociatedProjects(projects);
      setSelectedProjects(projects.map((p) => p.id));
    }
  }, [study, allProjects]);
  if (isStudyLoading || isProjectsLoading) return <Text>Loading...</Text>;
  if (isStudyError) return <Text>Error loading data</Text>;
  if (!study || !shipData) return <Text>No study or ship data available</Text>;

  const sailPositions = study?.form_values?.sailPositions || {};
  console.log(study);
  const wings = Object.entries(sailPositions).map(([label, sail]: any) => ({
    id: label,
    label,
    x: sail.position_m.y,
    y: sail.position_m.x,
    color: "#ff0000",
    model: sail.type,
  }));

  // Set up scaling factors
  const shipWidth = shipData?.vessel_data?.dtwin?.vessel?.beam_m ?? 50;
  const shipHeight =
    shipData?.vessel_data?.dtwin?.vessel?.length_over_all_m ?? 300;
  const canvasWidth = 200;
  const canvasHeight = 400;
  const scaleX = canvasWidth / shipWidth;
  const scaleY = canvasHeight / shipHeight;

  const confirmDeleteStudy = async (studyId: string) => {
    try {
      // const token = await getToken();
      await deleteStudyApi(studyId);
      setIsDeleteStudyModalOpen(false);
      navigate("/");
    } catch (error) {
      console.error("Error deleting study:", error);
    }
  };

  const handleDeleteStudy = (study: StudyDataType) => {
    setDeleteStudyId(study.id);
    setIsDeleteStudyModalOpen(true);
  };
  const handleProjectSelectionChange = (selected: string[]) => {
    setSelectedProjects(selected);
    setIsProjectUpdateModalOpen(true);
  };
  const confirmProjectAssociation = () => {
    setIsProjectUpdateModalOpen(false);
    const addedProjects = selectedProjects.filter(
      (id) => !associatedProjects.some((p) => p.id === id),
    );
    const removedProjects = associatedProjects
      .map((p) => p.id)
      .filter((id) => !selectedProjects.includes(id));

    for (const projectId of addedProjects) {
      associateStudyMutation.mutate(
        { study, projectId },
        {
          onSuccess: () => {
            const newProject = allProjects.find(
              (proj) => proj.id === projectId,
            );
            if (newProject) {
              setAssociatedProjects((prev) => [...prev, newProject]);
            }
          },
        },
      );
    }

    for (const projectId of removedProjects) {
      removeStudyMutation.mutate(
        { study, projectId },
        {
          onSuccess: () => {
            setAssociatedProjects((prev) =>
              prev.filter((proj) => proj.id !== projectId),
            );
          },
        },
      );
    }

    notifications.show({
      title: "Updated successfully!",
      message: "Study project associations updated successfully.",
      color: "green",
    });
  };
  const handleRemoveStudyFromProject = (projectId: string) => {
    removeStudyMutation.mutate(
      { study, projectId },
      {
        onSuccess: () => {
          setAssociatedProjects((prev) =>
            prev.filter((p) => p.id !== projectId),
          );
          notifications.show({
            title: "Study removed from project",
            message: `${study.experiment_name} has been removed from the project.`,
            color: "green",
          });
        },
      },
    );
  };
  // const handleRemoveStudyFromProject = async (projectId: string) => {
  //   try {
  //     const token = await getToken();
  //     if (token) {
  //       // Remove the study from the selected project
  //       await axios.delete(
  //         `${API_BASE_URL}/projects/${projectId}/studies/${study.id}`,
  //         { headers: { Authorization: `Bearer ${token}` } },
  //       );
  //       notifications.show({
  //         title: "Study removed from project",
  //         message: `${study.experiment_name} has been removed from the project.`,
  //         color: "green",
  //       });
  //
  //       // Update the state by removing the dissociated project from the list
  //       setAssociatedProjects(
  //         associatedProjects.filter((project) => project.id !== projectId),
  //       );
  //     }
  //   } catch (error) {
  //     console.error("Failed to remove study from project", error);
  //     notifications.show({
  //       title: "Error",
  //       message: "An error occurred while removing the study from the project.",
  //       color: "red",
  //     });
  //   }
  // };

  if (transition) return null;

  return (
    <div className={styles.container}>
      <div className={styles.gridContainer}>
        <div
          className={styles.innerContainer}
          style={{
            backgroundColor: colors.gray,
            padding: "1rem",
            borderRadius: ".5rem",
            boxShadow: "3px 3px 1rem rgba(0, 0, 0, .1)",
            height: "fit-content",
          }}
        >
          {/* General Info */}
          <div>
            <Text size="large">{study.experiment_name}</Text>
            <Text size="small">
              <strong>Vessel Type:</strong> {study.vessel_type}
            </Text>
            <Text size="small">
              <strong>Vessel Size:</strong> {study.vessel_size}
            </Text>
            <Text size="small">
              <strong>Comment:</strong> {study?.form_values?.comment || "N/A"}
            </Text>

            <Divider my="md" />
            {/* Associated Projects */}

            <Group justify="space-between">
              <Text size="large">Associated Projects</Text>
              <ActionIcon
                onClick={() => setIsEditingProjects(true)}
                variant="subtle"
                styles={{
                  root: {
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "transparent",
                      filter: "none",
                    },
                  },
                }}
              >
                <IconEdit
                  size={16}
                  style={{ color: colors.brand, transition: "none" }}
                />
              </ActionIcon>
            </Group>
            {isEditingProjects ? (
              <MultiSelect
                label="Projects"
                placeholder="Select projects"
                data={allProjects
                  .filter(
                    (p) => !associatedProjects.some((ap) => ap.id === p.id),
                  )
                  .map((p) => ({ value: p.id, label: p.name }))}
                value={selectedProjects}
                onChange={handleProjectSelectionChange}
              />
            ) : associatedProjects.length > 0 ? (
              associatedProjects.map((project) => (
                <Group key={project.id} justify="space-between" mb="md">
                  <Text size="small">{project.name}</Text>
                  <Button
                    variant="light"
                    size="xs"
                    onClick={() => handleRemoveStudyFromProject(project.id)}
                    style={[
                      deleteButtonStyles,
                      { backgroundColor: colors.red },
                    ]}
                  >
                    Remove
                  </Button>
                </Group>
              ))
            ) : (
              <Text size="small">No projects associated</Text>
            )}
            <Divider my="md" />
            <Text size="large">Study Management</Text>
            {/* Polar Plot Link */}
            <Group mt="md" mb="md">
              <Button
                component="a"
                href={study.result_link}
                target="_blank"
                style={buttonStyles}
              >
                View Polar Plot
              </Button>
            </Group>

            {/* Delete Button */}
            <Group mt="md">
              <Button
                variant="light"
                color="red"
                onClick={() => handleDeleteStudy(study)}
                leftSection={<IconTrash size={16} />}
                style={[deleteButtonStyles, { backgroundColor: colors.red }]}
              >
                Delete Study
              </Button>
            </Group>
          </div>
        </div>

        <div
          className={styles.innerContainer}
          style={{
            backgroundColor: colors.gray,
            padding: "1rem",
            borderRadius: ".5rem",
            boxShadow: "3px 3px 1rem rgba(0, 0, 0, .1)",
            marginBottom: "9rem",
          }}
        >
          {/* Wingsail Configuration */}
          <Title
            size="lg"
            mt="md"
            mb="sm"
            style={{
              color: getColor("title"),
            }}
          >
            Wingsail Configuration
          </Title>
          <div className={styles.wingsContainer}>
            <div>
              {/* Wings Cards */}
              <Accordion
                value={activeAccordionItem}
                onChange={setActiveAccordionItem}
                styles={accordionStyles}
              >
                {wings.map((wing) => (
                  <Accordion.Item
                    key={wing.id}
                    value={wing.id}
                    style={{ color: getColor("title") }}
                  >
                    <Accordion.Control>{wing.label}</Accordion.Control>
                    <Accordion.Panel>
                      <WingCardComponent
                        id={wing.id}
                        label={wing.label}
                        x={wing.y}
                        y={wing.x}
                        color={""}
                        model={wing.model}
                        models={[]}
                        onRemove={() => {}}
                        onUpdate={() => {}}
                        onClone={() => {}}
                        scaleX={scaleX}
                        scaleY={scaleY}
                        minX={-shipWidth / 2}
                        maxX={shipWidth / 2}
                        minY={0}
                        maxY={shipHeight}
                        readOnly={true}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            </div>
            <div className={styles.canvas}>
              {/* Canvas */}
              <CanvasComponent
                wings={wings}
                width={canvasWidth}
                height={canvasHeight}
                onDragEnd={() => {}}
                onDragStart={() => {}}
                onWingClick={(id) => setActiveAccordionItem(id)}
                scaleX={scaleX}
                scaleY={scaleY}
                shipWidth={shipWidth}
                shipHeight={shipHeight}
                draggable={true}
              />
            </div>
          </div>
          {/* Ship Details */}
          <Divider my="md" />
          <Text>Ship Details</Text>
          {shipData && (
            <>
              <ShipDataTable data={shipData.vessel_data} />
              <Divider my="md" />
              <PlotlyGraphs data={shipData.vessel_data} />
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <Modal
        opened={isDeleteStudyModalOpen}
        onClose={() => setIsDeleteStudyModalOpen(false)}
        title="Confirm Deletion"
      >
        <Blockquote color="red">
          <Text>
            Are you sure you want to delete this study? This action cannot be
            undone.
          </Text>
        </Blockquote>
        <Group mt="md">
          <div className={styles.btnContainer}>
            <button
              onClick={() => setIsDeleteStudyModalOpen(false)}
              className={styles.button}
              style={{
                backgroundColor: colors.darkGray,
                color: colors.primary,
              }}
            >
              Cancel
            </button>

            <button
              onClick={() => deleteStudyId && confirmDeleteStudy(deleteStudyId)}
              className={styles.button}
              style={{ backgroundColor: colors.red, color: colors.primary }}
            >
              Delete
            </button>
          </div>
        </Group>
      </Modal>

      <Modal
        opened={isProjectUpdateModalOpen}
        onClose={() => setIsProjectUpdateModalOpen(false)}
        title="Confirm Project Association"
      >
        <Blockquote color="blue">
          Do you want to update the selected projects for this study?
        </Blockquote>
        <Group mt="md">
          <Button onClick={() => setIsProjectUpdateModalOpen(false)}>
            Cancel
          </Button>
          <Button color="blue" onClick={confirmProjectAssociation}>
            Update
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default StudyDetails;
